import React, { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Button,
} from '@mui/material'
import { Search, Sparkles, ArrowLeft } from 'lucide-react'
import {
  useGetNicheCategoriesQuery,
  useSearchNicheContentLazyQuery,
  useBrowseNicheContentLazyQuery,
  Platform,
  NicheSortBy,
} from 'graphql/generated/graphql'
import NicheGrid from './components/NicheGrid'
import ContentFeed from './components/ContentFeed'

export default function DiscoverContent() {
  const [selectedNiche, setSelectedNiche] = useState<{ id: string; name: string; emoji: string } | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | ''>('')
  const [sortBy, setSortBy] = useState<NicheSortBy>(NicheSortBy.Recent)
  const [offset, setOffset] = useState(0)
  const [allContent, setAllContent] = useState<any[]>([])
  const [hasMore, setHasMore] = useState(true)
  const ITEMS_PER_PAGE = 50

  // Fetch niche categories
  const { data: nichesData, loading: nichesLoading, error: nichesError } = useGetNicheCategoriesQuery()

  // Lazy queries for search and browse
  const [executeSearch, { data: searchData, loading: searchLoading }] = useSearchNicheContentLazyQuery()
  const [executeBrowse, { data: browseData, loading: browseLoading }] = useBrowseNicheContentLazyQuery()

  // Helper interface for browse results
  interface BrowseResultData { data?: typeof browseData }

  const handleSelectNiche = (niche: { id: string; name: string; emoji: string }) => {
    setSelectedNiche(niche)
    setSearchQuery('')
    setOffset(0)
    setAllContent([])
    setHasMore(true)
    // Fetch initial content for this niche
    void executeBrowse({
      variables: {
        input: {
          nicheCategoryId: niche.id,
          platform: selectedPlatform === '' ? undefined : selectedPlatform,
          sortBy: sortBy,
          limit: ITEMS_PER_PAGE,
          offset: 0,
        },
      },
    }).then((result: BrowseResultData) => {
      const content = result.data?.browseNicheContent?.content ?? []
      setAllContent(content)
      setHasMore(result.data?.browseNicheContent?.hasMore ?? false)
    })
  }

  const handleBackToNiches = () => {
    setSelectedNiche(null)
    setSearchQuery('')
    setOffset(0)
    setAllContent([])
    setHasMore(true)
  }

  const handleSearch = () => {
    if (!selectedNiche || !searchQuery.trim()) return
    setAllContent([]) // Clear browse content when searching

    void executeSearch({
      variables: {
        input: {
          nicheCategoryId: selectedNiche.id,
          query: searchQuery,
          platform: selectedPlatform === '' ? undefined : selectedPlatform,
          limit: ITEMS_PER_PAGE,
        },
      },
    })
  }

  const handleFilterChange = () => {
    if (!selectedNiche) return
    setOffset(0)
    setAllContent([])
    setHasMore(true)

    if (searchQuery.trim()) {
      handleSearch()
    } else {
      void executeBrowse({
        variables: {
          input: {
            nicheCategoryId: selectedNiche.id,
            platform: selectedPlatform === '' ? undefined : selectedPlatform,
            sortBy: sortBy,
            limit: ITEMS_PER_PAGE,
            offset: 0,
          },
        },
      }).then((result: BrowseResultData) => {
        const content = result.data?.browseNicheContent?.content ?? []
        setAllContent(content)
        setHasMore(result.data?.browseNicheContent?.hasMore ?? false)
      })
    }
  }

  const handleLoadMore = () => {
    if (!selectedNiche || browseLoading || !hasMore) return
    const newOffset = offset + ITEMS_PER_PAGE

    void executeBrowse({
      variables: {
        input: {
          nicheCategoryId: selectedNiche.id,
          platform: selectedPlatform === '' ? undefined : selectedPlatform,
          sortBy: sortBy,
          limit: ITEMS_PER_PAGE,
          offset: newOffset,
        },
      },
    }).then((result: BrowseResultData) => {
      const newContent = result.data?.browseNicheContent?.content ?? []
      setAllContent((prev: typeof newContent) => [...prev, ...newContent])
      setOffset(newOffset)
      setHasMore(result.data?.browseNicheContent?.hasMore ?? false)
    })
  }

  // Get content to display - use allContent for browse, searchData for search
  const content = searchQuery.trim() && searchData?.searchNicheContent?.content
    ? searchData.searchNicheContent.content
    : allContent.length > 0
    ? allContent
    : browseData?.browseNicheContent?.content ?? []

  const isLoading = searchLoading || browseLoading

  // Handle subscription error
  if (nichesError?.message?.includes('paid subscribers')) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '80px',
            height: '80px',
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
          }}
        >
          <Sparkles size={40} color="white" />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', marginBottom: '12px' }}>
          Discover Content
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', maxWidth: '400px', marginBottom: '24px' }}>
          This feature is available for paid subscribers only. Upgrade your plan to browse and search content by niche.
        </Typography>
        <Button
          variant="contained"
          href="/subscription"
          sx={{
            background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
            padding: '12px 32px',
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Upgrade Plan
        </Button>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        padding: '32px',
      }}
    >
      <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <Box sx={{ marginBottom: '32px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            {selectedNiche && (
              <IconButton onClick={handleBackToNiches} sx={{ color: 'white' }}>
                <ArrowLeft size={24} />
              </IconButton>
            )}
            <Box
              sx={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Sparkles size={24} color="white" />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                {selectedNiche ? `${selectedNiche.emoji} ${selectedNiche.name}` : 'Discover Content'}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                {selectedNiche
                  ? 'Search with natural language or browse trending content'
                  : 'Browse by niche or search with natural language'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Niche Selection */}
        {!selectedNiche && (
          <>
            {nichesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
                <CircularProgress sx={{ color: '#a855f7' }} />
              </Box>
            ) : (
              <NicheGrid
                niches={nichesData?.getNicheCategories ?? []}
                onSelectNiche={handleSelectNiche}
              />
            )}
          </>
        )}

        {/* Selected Niche View */}
        {selectedNiche && (
          <>
            {/* Search and Filters */}
            <Box sx={{ display: 'flex', gap: 2, marginBottom: '24px', flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                placeholder="Search with natural language... (e.g., 'home workouts for beginners')"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                sx={{
                  flex: 1,
                  minWidth: '300px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderRadius: '12px',
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                    '&.Mui-focused fieldset': { borderColor: '#a855f7' },
                  },
                  '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)' },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={20} color="#a855f7" />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl sx={{ minWidth: '150px' }}>
                <Select
                  value={selectedPlatform}
                  onChange={(e) => {
                    setSelectedPlatform(e.target.value as Platform | '')
                    setTimeout(handleFilterChange, 0)
                  }}
                  displayEmpty
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderRadius: '12px',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#a855f7' },
                  }}
                >
                  <MenuItem value="">All Platforms</MenuItem>
                  <MenuItem value={Platform.Instagram}>Instagram</MenuItem>
                  <MenuItem value={Platform.TikTok}>TikTok</MenuItem>
                  <MenuItem value={Platform.Youtube}>YouTube</MenuItem>
                  <MenuItem value={Platform.Reddit}>Reddit</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: '140px' }}>
                <Select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value as NicheSortBy)
                    setTimeout(handleFilterChange, 0)
                  }}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    borderRadius: '12px',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#a855f7' },
                  }}
                >
                  <MenuItem value={NicheSortBy.Recent}>Most Recent</MenuItem>
                  <MenuItem value={NicheSortBy.Popular}>Most Popular</MenuItem>
                  <MenuItem value={NicheSortBy.Engagement}>Highest Engagement</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={!searchQuery.trim()}
                sx={{
                  background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  padding: '12px 24px',
                  '&:disabled': {
                    background: 'rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.3)',
                  },
                }}
              >
                Search
              </Button>
            </Box>

            {/* Results Info */}
            {searchQuery.trim() && searchData?.searchNicheContent && (
              <Box sx={{ marginBottom: '16px' }}>
                <Chip
                  label={`${content.length} results for "${searchQuery}"`}
                  sx={{
                    backgroundColor: 'rgba(168, 85, 247, 0.2)',
                    color: '#a855f7',
                    fontWeight: 500,
                  }}
                />
              </Box>
            )}

            {/* Content Feed */}
            {isLoading && content.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
                <CircularProgress sx={{ color: '#a855f7' }} />
              </Box>
            ) : content.length > 0 ? (
              <>
                <ContentFeed content={content} />

                {/* Load More Button - only show for browse (not search) and when there's more content */}
                {!searchQuery.trim() && hasMore && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '32px' }}>
                    <Button
                      variant="outlined"
                      onClick={handleLoadMore}
                      disabled={browseLoading}
                      sx={{
                        borderColor: 'rgba(168, 85, 247, 0.5)',
                        color: '#a855f7',
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: '12px 32px',
                        '&:hover': {
                          borderColor: '#a855f7',
                          backgroundColor: 'rgba(168, 85, 247, 0.1)',
                        },
                        '&:disabled': {
                          borderColor: 'rgba(255,255,255,0.1)',
                          color: 'rgba(255,255,255,0.3)',
                        },
                      }}
                    >
                      {browseLoading ? (
                        <CircularProgress size={20} sx={{ color: '#a855f7', marginRight: '8px' }} />
                      ) : null}
                      {browseLoading ? 'Loading...' : 'Load More'}
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <Box
                sx={{
                  textAlign: 'center',
                  padding: '48px',
                  backgroundColor: 'rgba(255,255,255,0.02)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255,255,255,0.05)',
                }}
              >
                <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>
                  {searchQuery.trim()
                    ? 'No results found. Try a different search query.'
                    : 'No content available for this niche yet. Check back soon!'}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}
